/**
 * In-app tracking event logger.
 *
 * Wraps window.dataLayer.push and classifies every event into:
 *   - GA4 e-commerce event (standard recommended events)
 *   - Google Ads conversion (purchase / lead / page_view send_to)
 *   - Other (engagement, custom, internal)
 *
 * Buffer is in-memory (ring, max 200) and exposed via:
 *   - subscribe(listener) for React UI
 *   - window.__zooiTrackingLog for console inspection
 *
 * This module ONLY observes — it never alters dataLayer payloads.
 * Safe to install alongside the existing denylist proxy in gtmDataLayer.ts
 * because we wrap whatever push currently exists.
 */

const GA4_ECOMMERCE_EVENTS = new Set([
  "view_item_list",
  "select_item",
  "view_item",
  "add_to_cart",
  "remove_from_cart",
  "view_cart",
  "begin_checkout",
  "add_shipping_info",
  "add_payment_info",
  "purchase",
  "refund",
  "add_to_wishlist",
  "select_promotion",
  "view_promotion",
]);

/**
 * Events that our GTM container maps (or is configured to map) to a
 * Google Ads conversion. Keep this in sync with the GTM trigger list.
 *
 * AW-18059753112
 *   - mE4lCNfGxpQcEJjtx6ND  (Purchase)        ← purchase / purchase_client_hint
 *   - generate_lead label   (Lead)            ← generate_lead
 *   - page_view (remarketing)                 ← always sent via gtag config
 */
const GOOGLE_ADS_CONVERSION_EVENTS = new Set([
  "purchase",
  "purchase_client_hint",
  "generate_lead",
  "sign_up",
]);

export type EventClassification =
  | "ga4_ecommerce"
  | "google_ads_conversion"
  | "ga4_engagement"
  | "consent"
  | "internal"
  | "other";

export interface LoggedEvent {
  ts: number;
  event: string;
  classifications: EventClassification[];
  /** Helpful identifiers extracted for quick diff with server logs */
  eventId?: string;
  transactionId?: string;
  value?: number;
  currency?: string;
  itemsCount?: number;
  /** Full original payload (shallow-cloned) */
  payload: Record<string, unknown>;
}

const MAX_BUFFER = 200;
const buffer: LoggedEvent[] = [];
const listeners = new Set<(events: LoggedEvent[]) => void>();
let installed = false;

function classify(eventName: string): EventClassification[] {
  const tags: EventClassification[] = [];
  if (GA4_ECOMMERCE_EVENTS.has(eventName)) tags.push("ga4_ecommerce");
  if (GOOGLE_ADS_CONVERSION_EVENTS.has(eventName)) tags.push("google_ads_conversion");
  if (eventName === "consent_update" || eventName === "consent_default") tags.push("consent");
  if (
    eventName === "set_user_data" ||
    eventName === "tracking_status_test" ||
    eventName.startsWith("gtm.")
  ) {
    tags.push("internal");
  }
  if (
    tags.length === 0 &&
    (eventName === "search" || eventName === "login" || eventName === "share")
  ) {
    tags.push("ga4_engagement");
  }
  if (tags.length === 0) tags.push("other");
  return tags;
}

function extractMeta(payload: Record<string, unknown>) {
  const ecom = (payload.ecommerce as Record<string, unknown> | undefined) ?? undefined;
  const items = (ecom?.items as unknown[] | undefined) ?? (payload.items as unknown[] | undefined);
  return {
    eventId: (payload.event_id as string | undefined) ?? undefined,
    transactionId:
      (payload.transaction_id as string | undefined) ??
      (ecom?.transaction_id as string | undefined),
    value:
      typeof payload.value === "number"
        ? (payload.value as number)
        : typeof ecom?.value === "number"
          ? (ecom!.value as number)
          : undefined,
    currency:
      (payload.currency as string | undefined) ?? (ecom?.currency as string | undefined),
    itemsCount: Array.isArray(items) ? items.length : undefined,
  };
}

function record(entry: unknown) {
  if (!entry || typeof entry !== "object") return;
  const obj = entry as Record<string, unknown>;
  const eventName = typeof obj.event === "string" ? obj.event : null;
  if (!eventName) return; // skip ecommerce-clear pushes etc.

  const meta = extractMeta(obj);
  const logged: LoggedEvent = {
    ts: Date.now(),
    event: eventName,
    classifications: classify(eventName),
    ...meta,
    payload: { ...obj },
  };

  buffer.push(logged);
  if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);

  // Notify subscribers (snapshot copy)
  const snapshot = buffer.slice();
  listeners.forEach((l) => {
    try { l(snapshot); } catch { /* swallow */ }
  });
}

export function installTrackingEventLogger() {
  if (installed) return;
  if (typeof window === "undefined") return;
  installed = true;

  const w = window as unknown as { dataLayer?: unknown[]; __zooiTrackingLog?: unknown };
  if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
  const dl = w.dataLayer as unknown[] & { push: (...args: unknown[]) => number };

  // Capture any pre-existing events
  for (const existing of dl) record(existing);

  const originalPush = dl.push.bind(dl);
  dl.push = (...args: unknown[]) => {
    const result = originalPush(...args);
    try { for (const a of args) record(a); } catch { /* swallow */ }
    return result;
  };

  // Console helper
  w.__zooiTrackingLog = {
    all: () => buffer.slice(),
    ga4: () => buffer.filter((e) => e.classifications.includes("ga4_ecommerce")),
    ads: () => buffer.filter((e) => e.classifications.includes("google_ads_conversion")),
    clear: () => { buffer.length = 0; listeners.forEach((l) => l([])); },
  };
}

export function subscribeTrackingEvents(listener: (events: LoggedEvent[]) => void): () => void {
  listeners.add(listener);
  listener(buffer.slice());
  return () => { listeners.delete(listener); };
}

export function getTrackingEventBuffer(): LoggedEvent[] {
  return buffer.slice();
}
